import './styles/globals.css';
import './i18n';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router';
import App from './App';

// React 19 ships native <title>/<meta>/<link> hoisting (React Docs §"Special Rendering
// Behavior"). We use that instead of react-helmet-async — peer-dep ranges for the
// helmet ecosystem still target React 18 and conflict with React 19. Phase 5 (SEO-01..06)
// will set per-route metadata directly via <title>, <meta>, etc. inside route components.

// WR-003: validate VITE_SITE_URL at boot, before any render runs. D-19 forbids
// a `?? fallback` default at the call site, so a missing env var would otherwise
// render `<link rel="alternate" href="undefined/" />` into <head> and break
// hreflang silently. Throwing here converts that silent regression into a loud
// build/boot misconfiguration signal — exactly what the project rule wants.
// VITE_API_BASE_URL is intentionally NOT validated here: Phase 2 doesn't read
// it yet; later phases that consume it will repeat this pattern at their own
// boot or first-use surface.
if (!import.meta.env.VITE_SITE_URL) {
  throw new Error(
    'VITE_SITE_URL is not set. hreflang URLs would render "undefined/" in <head>. ' +
      'Set the env var and rebuild — see .env.example.',
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root element not found');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
